/**
 * Shared meta-tag builder for storefront content pages (about, faq,
 * contact, shipping, returns, accessibility, etc.).
 *
 * Standardises the og:* + twitter:* + canonical + locale tags so we
 * don't have a different shape on every page (audit 2026-04-30 SEO
 * sweep flagged drift between pages).
 *
 * Pure (no I/O) — safe to import from .tsx files where meta runs on
 * both server and client.
 */
import type { Locale } from "~/utils/locale";

export interface StorefrontPageMeta {
  /** Page title — already locale-aware. */
  title: string;
  /** Meta description — already locale-aware. */
  description: string;
  /** Locale-aware path, e.g. "/about" or "/en/about". */
  path: string;
  /** Origin from the request (https://diolo.com or test.* on staging). */
  origin: string;
  /** Locale (vi or en). */
  locale: Locale;
  /** Optional og:image URL. Defaults to the brand wordmark. */
  ogImage?: string;
  /** Optional og:type. Defaults to "website". */
  ogType?: string;
}

export function buildPageMeta(input: StorefrontPageMeta) {
  const ogImage = input.ogImage ?? `${input.origin}/og/diolo-v2.png`;
  const ogType = input.ogType ?? "website";
  const canonicalUrl = `${input.origin}${input.path}`;
  const ogLocale = input.locale === "en" ? "en_US" : "vi_VN";

  return [
    { title: input.title },
    { name: "description", content: input.description },
    { tagName: "link", rel: "canonical", href: canonicalUrl },
    // Open Graph
    { property: "og:title", content: input.title },
    { property: "og:description", content: input.description },
    { property: "og:type", content: ogType },
    { property: "og:url", content: canonicalUrl },
    { property: "og:image", content: ogImage },
    { property: "og:locale", content: ogLocale },
    { property: "og:site_name", content: "Diolo" },
    // Twitter card
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: input.title },
    { name: "twitter:description", content: input.description },
    { name: "twitter:image", content: ogImage },
  ];
}

/**
 * BreadcrumbList JSON-LD generator. Pass the breadcrumb trail; we
 * emit the schema.org structured data Remix can render via the
 * special "script:ld+json" meta type.
 */
export function buildBreadcrumbJsonLd(
  origin: string,
  trail: Array<{ name: string; path?: string }>,
) {
  return {
    "script:ld+json": {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: trail.map((t, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: t.name,
        ...(t.path ? { item: `${origin}${t.path}` } : {}),
      })),
    },
  };
}

/** Compute the request origin, normalised to https://. */
export function originFromRequest(request: Request): string {
  return new URL(request.url).origin.replace(/^http:\/\//, "https://");
}
